/**
 * Shared storefront hero band (TNK-204). One band, used by both the /shop
 * landing and /shop/inventory so the banner is consistent. Operator-configurable:
 * `heroHeight` (px) sets a fixed band height and `heroOpacity` (0–100) dims the
 * image; both fall back to app defaults so existing stores keep ~today's look.
 *
 * The image fills the fixed-height band via object-cover (no aspect surprises);
 * with no image the band shows the CSS-gradient fallback at the same height —
 * so the landing's empty space is filled either way. Negative horizontal margins
 * escape the parent's px-4 / sm:px-6 so the band reads full-bleed inside the
 * max-w-6xl shell. Children render as the bottom-aligned overlay content.
 */

import { imageUrl } from "@/features/imageUpload";

const DEFAULT_HEIGHT = 280;
const DEFAULT_OPACITY = 50;

export function HeroBand({
  heroRef,
  heroHeight,
  heroOpacity,
  heroFit,
  contentClassName = "justify-end",
  children,
}: {
  heroRef: string | null;
  heroHeight: number | null;
  heroOpacity: number | null;
  heroFit: string | null;
  /** Tailwind alignment utilities for the overlay content (justify / items /
   *  text). Defaults to bottom-aligned; the landing passes its title position. */
  contentClassName?: string;
  children: React.ReactNode;
}) {
  const heroUrl = heroRef ? imageUrl(heroRef) : null;
  const height = heroHeight ?? DEFAULT_HEIGHT;
  const opacity = (heroOpacity ?? DEFAULT_OPACITY) / 100;
  const objectFit = (
    heroFit === "contain" || heroFit === "fill" ? heroFit : "cover"
  ) as "cover" | "contain" | "fill";

  return (
    <section
      className="relative -mx-4 mb-6 overflow-hidden border-b border-border sm:-mx-6"
      style={{ height: `${height}px`, background: "var(--shop-hero-bg)" }}
    >
      {heroUrl && (
        <img
          src={heroUrl}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full select-none"
          style={{ opacity, objectFit, objectPosition: "center" }}
        />
      )}
      {/* Soft gold vignette */}
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(circle at 50% 100%, rgba(201,168,76,0.18) 0%, transparent 60%)",
        }}
      />
      {/* Bottom darken so overlay text stays legible over any image */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, transparent 35%, rgba(0,0,0,0.55) 100%)",
        }}
      />

      <div
        className={`relative z-10 mx-auto flex h-full max-w-6xl flex-col px-4 pb-6 sm:px-6 ${contentClassName}`}
      >
        {children}
      </div>
    </section>
  );
}
